import React, { useState, useEffect, useMemo, useCallback, Key } from 'react';
import { TOrganizationByIdUI } from 'data/types/organizations.types';
import {
  Card,
  ENotificationType,
  useShowToastNotification,
  useTranslation,
} from '@packages/utils';
import dayjs from 'dayjs';
import { Table } from 'components/Base/Table';
import {
  TColumnType,
  TColumnsState,
  ETableStoragePaths,
} from 'components/Base/Table/Table.types';
import { Space } from 'components/Base/Space';
import Button from 'components/Base/Button/Button';
import { Flex } from 'antd';
import styled from 'styled-components';
import { NotebookOutline, RefreshOutline } from 'assets/icon/icon';
import PlusMinusInput from 'components/Base/PlusMinusInput/PlusMinusInput';
import {
  useGetOrganizationKitInventoryQuery,
  useGetOrganizationKitInventoryByCategoryQuery,
  useUpdateOrganizationKitInventoryMutation,
  useExportOrganizationKitInventoryMutation,
} from 'redux/apiSlice/organizationKitInventory.slice';
import type { WorkshopKitCategory } from 'redux/apiSlice/organizationKitInventory.slice';
import { useCheckPermission } from 'hooks/useCheckPermission';
import ExportDropdown from 'components/Shared/ExportDropdown';
import { appLocalStorage, EStorageNames } from 'helpers/storage';
import NotesModal from 'pages/private/Workshop/components/WorkShopDetails/KitsTab/NotesModal';
import OrderHistoryModal from 'pages/private/Workshop/components/WorkShopDetails/KitsTab/OrderHistoryModal';

const TabsContainer = styled.div`
  display: flex;
  align-items: flex-start;
  margin-bottom: 16px;
`;

interface TabButtonProps {
  isActive: boolean;
  isFirst: boolean;
  isLast: boolean;
}

const TabButton = styled.button<TabButtonProps>`
  padding: 8px 16px;
  background-color: ${({ isActive, theme }) =>
    isActive ? theme.colorPrimaryDefault : theme.colorWhitePrimary};
  color: ${({ isActive, theme }) =>
    isActive ? theme.colorWhitePrimary : theme.colorBlackDescription};
  cursor: pointer;
  font-family: Lato;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 24px;
  border: 1px solid ${({ theme }) => theme.colorBlackMainBorder};
  border-left: ${({ isFirst, theme }) =>
    isFirst ? `1px solid ${theme.colorBlackMainBorder}` : 'none'};
  border-radius: ${({ isFirst, isLast }) => {
    if (isFirst) return '8px 0 0 8px';
    if (isLast) return '0 8px 8px 0';
    return '0';
  }};
  text-align: center;
`;

const OrganizationNotesModal: React.FC<{
  visible: boolean;
  onClose: () => void;
  organizationId: string;
}> = ({ visible, onClose, organizationId }) => {
  return (
    <NotesModal
      visible={visible}
      onClose={onClose}
      workshopId={organizationId}
      partId="organization-kit-inventory"
      translationKeyPrefix="descriptions.admin-portal.organizations.details.kits"
      useOrganizationEndpoints
    />
  );
};

const TRANSLATION_KEY = 'descriptions.admin-portal.organizations.details.kits';
const LOCAL_STORAGE_KEY = 'organization_kit_inventory_table_params';
const ACTIVE_TAB_STORAGE_KEY = 'organization_kit_inventory_active_tab';

// If categories change, update this array and the WorkshopKitCategory type
const KIT_INVENTORY_TABS = [
  { key: 'ALL', label: 'All' },
  { key: 'RFK_PART_ONE', label: 'RFK Part 1' },
  { key: 'RFK_PART_TWO', label: 'RFK Part 2' },
  { key: 'RFK_PART_THREE', label: 'RFK Part 3' },
  { key: 'SEL_MATH', label: 'SEL/Math' },
  { key: 'CCP', label: 'CCP' },
];

const COLUMN_KEY_TO_EXPORT_NAME: Record<string, string> = {
  resourceIds: 'kitNameAndLanguage',
  ageGroup: 'ageGroup',
  language: 'language',
  inventory: 'qtyOnHand',
  lastReceivedDate: 'lastReceived',
};

const SORT_FIELD_MAP: Record<string, string> = {
  inventory: 'qtyOnHand',
  lastReceivedDate: 'lastReceived',
};

interface OrganizationKitsInventoryManagementTabProps {
  detailsData?: TOrganizationByIdUI;
}

interface TKitInventoryItem {
  key: string;
  name: string;
  ageGroup: string;
  ageGroupEnum: string;
  language: string;
  inventory: number;
  lastReceivedDate: string;
}

interface TableFilters {
  resourceIds?: string[];
  ageGroup?: string[];
  language?: string[];
  [key: string]: any;
}

interface TableParams {
  pagination: {
    current: number;
    pageSize: number;
    total?: number;
  };
  filters: TableFilters;
  sorter: {
    field: string;
    order: 'ascend' | 'descend' | null;
  };
  organizationId?: string;
}

interface FilterOption {
  text: string;
  value: string;
}

// Define language options
const LANGUAGE_OPTIONS: FilterOption[] = [
  { text: 'ENGLISH', value: 'ENGLISH' },
  { text: 'SPANISH', value: 'SPANISH' },
];

// Map to convert formatted strings to enum values if needed
const AGE_GROUP_MAP: Record<string, string> = {
  '0-1': 'ZERO_TO_ONE',
  '1-2': 'ONE_TO_TWO',
  '2-3': 'TWO_TO_THREE',
  '3-4': 'THREE_TO_FOUR',
  '2-5': 'TWO_TO_FIVE',
  '3-5': 'THREE_TO_FIVE',
  '4-5': 'FOUR_TO_FIVE',
};

const DEFAULT_TABLE_PARAMS: TableParams = {
  pagination: {
    current: 1,
    pageSize: 10,
  },
  filters: {},
  sorter: {
    field: '',
    order: null,
  },
};

const OrganizationKitsInventoryManagementTab: React.FC<
  OrganizationKitsInventoryManagementTabProps
> = ({ detailsData }) => {
  const { t } = useTranslation({
    keyPrefix: TRANSLATION_KEY,
  });

  const [activeTab, setActiveTab] = useState<string>(() => {
    try {
      return localStorage.getItem(ACTIVE_TAB_STORAGE_KEY) || 'ALL';
    } catch {
      return 'ALL';
    }
  });
  const isAllTab = activeTab === 'ALL';

  const canEdit = useCheckPermission('PART_ORDER_EDIT');
  const { showToastNotification } = useShowToastNotification();
  const [kitInventoryData, setKitInventoryData] = useState<TKitInventoryItem[]>(
    [],
  );
  const [totalElements, setTotalElements] = useState(0);
  const [userSortedFields, setUserSortedFields] = useState<string[]>([]);

  const [editedValues, setEditedValues] = useState<Record<string, number>>({});
  const [updatingKey, setUpdatingKey] = useState<string | null>(null);
  const [allFilters, setAllFilters] = useState<{
    resourceIds: FilterOption[];
    ageGroup: FilterOption[];
    language: FilterOption[];
  }>({
    resourceIds: [],
    ageGroup: [],
    language: LANGUAGE_OPTIONS,
  });

  const [tableParams, setTableParams] = useState<TableParams>(() => {
    try {
      const savedParams = localStorage.getItem(LOCAL_STORAGE_KEY);
      if (savedParams) {
        const parsedParams = JSON.parse(savedParams) as TableParams;
        return parsedParams;
      }
    } catch (error) {
      console.error('Error loading saved table params:', error);
    }
    return DEFAULT_TABLE_PARAMS;
  });
  const [isNotesModalVisible, setIsNotesModalVisible] = useState(false);
  const [isHistoryModalVisible, setIsHistoryModalVisible] = useState(false);

  // Reset filters if organization ID changes
  useEffect(() => {
    if (detailsData?.id) {
      if (
        tableParams.organizationId &&
        tableParams.organizationId !== detailsData.id
      ) {
        setTableParams(prev => ({
          pagination: {
            ...prev.pagination,
            current: 1,
          },
          filters: {},
          sorter: prev.sorter,
          organizationId: detailsData.id,
        }));
      } else if (!tableParams.organizationId) {
        setTableParams(prev => ({
          ...prev,
          organizationId: detailsData.id,
        }));
      }
    }
  }, [detailsData?.id]);

  useEffect(() => {
    try {
      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(tableParams));
    } catch (error) {
      console.error('Error saving table params:', error);
    }
  }, [tableParams]);

  const getBackendSortField = (field: string): string => {
    const fieldMap: Record<string, string> = {
      inventory: 'inventory',
      lastReceivedDate: 'lastReceivedDate',
    };
    return fieldMap[field] || '';
  };

  const queryParams = {
    organizationId: detailsData?.id || '',
    pageSize: tableParams.pagination.pageSize,
    page: tableParams.pagination.current - 1,
    ...(userSortedFields.length > 0 &&
    tableParams.sorter.field &&
    tableParams.sorter.order
      ? {
          sortField: getBackendSortField(tableParams.sorter.field),
          sortDirection:
            tableParams.sorter.order === 'descend' ? 'DESC' : 'ASC',
        }
      : {}),
    resourceIds: tableParams.filters.resourceIds?.join(','),
    language: tableParams.filters.language?.join(','),
    ageGroup: tableParams.filters.ageGroup?.join(','),
  };

  const {
    data: allData,
    isLoading: allLoading,
    isFetching: allFetching,
  } = useGetOrganizationKitInventoryQuery(queryParams, {
    skip: !detailsData?.id || !isAllTab,
  });

  const {
    data: categoryData,
    isLoading: catLoading,
    isFetching: catFetching,
  } = useGetOrganizationKitInventoryByCategoryQuery(
    { ...queryParams, category: activeTab as WorkshopKitCategory },
    { skip: !detailsData?.id || isAllTab },
  );

  const organizationKitInventoryData = isAllTab ? allData : categoryData;
  const isKitInventoryLoading = isAllTab ? allLoading : catLoading;
  const isKitInventoryFetching = isAllTab ? allFetching : catFetching;

  const [updateKitInventory, { isLoading: isUpdating }] =
    useUpdateOrganizationKitInventoryMutation();

  const [exportKitInventory, { isLoading: isExporting }] =
    useExportOrganizationKitInventoryMutation();

  const [selectedRows, setSelectedRows] = useState<{
    selectedRowsKeys: Key[];
    selectedRowsRecords: unknown[];
  }>({
    selectedRowsKeys: [],
    selectedRowsRecords: [],
  });

  useEffect(() => {
    if (organizationKitInventoryData) {
      try {
        if (organizationKitInventoryData.filters) {
          const resourceOptions: FilterOption[] = Object.entries(
            organizationKitInventoryData.filters.kits || {},
          ).map(([id, name]) => ({
            text: String(name),
            value: id,
          }));

          const ageGroupOptions: FilterOption[] = Object.entries(
            organizationKitInventoryData.filters.ageGroup || {},
          ).map(([enumValue, displayValue]) => ({
            text: String(displayValue),
            value: enumValue,
          }));

          const languageOptions: FilterOption[] = Object.entries(
            organizationKitInventoryData.filters.language || {},
          ).map(([key, value]) => ({
            text: String(value),
            value: key,
          }));

          setAllFilters({
            resourceIds: resourceOptions,
            ageGroup: ageGroupOptions,
            language:
              languageOptions.length > 0 ? languageOptions : LANGUAGE_OPTIONS,
          });
          const updatedFilters: TableFilters = { ...tableParams.filters };
          let filtersChanged = false;

          if (
            updatedFilters.resourceIds &&
            updatedFilters.resourceIds.length > 0
          ) {
            const validResourceIds = updatedFilters.resourceIds.filter(id =>
              resourceOptions.some(option => option.value === id),
            );

            if (validResourceIds.length !== updatedFilters.resourceIds.length) {
              updatedFilters.resourceIds =
                validResourceIds.length > 0 ? validResourceIds : undefined;
              filtersChanged = true;
            }
          }

          if (updatedFilters.ageGroup && updatedFilters.ageGroup.length > 0) {
            const validAgeGroups = updatedFilters.ageGroup.filter(group =>
              ageGroupOptions.some(option => option.value === group),
            );

            if (validAgeGroups.length !== updatedFilters.ageGroup.length) {
              updatedFilters.ageGroup =
                validAgeGroups.length > 0 ? validAgeGroups : undefined;
              filtersChanged = true;
            }
          }

          if (updatedFilters.language && updatedFilters.language.length > 0) {
            const validLanguages = updatedFilters.language.filter(lang =>
              languageOptions.some(option => option.value === lang),
            );

            if (validLanguages.length !== updatedFilters.language.length) {
              updatedFilters.language =
                validLanguages.length > 0 ? validLanguages : undefined;
              filtersChanged = true;
            }
          }

          if (filtersChanged) {
            setTableParams(prev => ({
              ...prev,
              filters: updatedFilters,
            }));
          }
        }
        if (
          organizationKitInventoryData.page &&
          organizationKitInventoryData.page.content
        ) {
          const data = organizationKitInventoryData.page.content.map(item => {
            const enumAgeGroup = item.ageGroup
              ? AGE_GROUP_MAP[item.ageGroup] || item.ageGroup
              : '';

            return {
              key: item.resourceId,
              name: item.name,
              ageGroup: item.ageGroup || 'N/A',
              ageGroupEnum: enumAgeGroup,
              language: item.language || 'N/A',
              inventory: item.inventory,
              lastReceivedDate: item.lastReceivedDate
                ? dayjs(item.lastReceivedDate).format('MMM DD, YYYY')
                : 'N/A',
            };
          });

          setKitInventoryData(data);
          // Initialize edited values with the fetched inventories
          const initialEdited: Record<string, number> = {};
          data.forEach(item => {
            initialEdited[item.key] = item.inventory;
          });
          setEditedValues(initialEdited);
          const totalItemsCount =
            organizationKitInventoryData.page.totalElements;
          setTotalElements(totalItemsCount);

          setTableParams(prev => ({
            ...prev,
            pagination: {
              ...prev.pagination,
              total: totalItemsCount,
            },
          }));
        }
      } catch (error) {
        console.error('Error processing API response:', error);
      }
    }
  }, [organizationKitInventoryData, tableParams.filters]);

  useEffect(() => {
    if (tableParams.sorter.field && tableParams.sorter.order) {
      setUserSortedFields(prev =>
        prev.includes(tableParams.sorter.field)
          ? prev
          : [...prev, tableParams.sorter.field],
      );
    }
  }, []);

  const hasFilters = useMemo(() => {
    return Object.keys(tableParams.filters).length > 0;
  }, [tableParams.filters]);

  const handleTableChange = (pagination: any, filters: any, sorter: any) => {
    const newSorter = Array.isArray(sorter) ? sorter[0] : sorter;

    if (newSorter && newSorter.field && newSorter.order) {
      setUserSortedFields(prev =>
        prev.includes(newSorter.field) ? prev : [...prev, newSorter.field],
      );
    }

    const updatedFilters = { ...filters };

    setTableParams({
      pagination,
      filters: updatedFilters,
      sorter: {
        field: newSorter.field || '',
        order: newSorter.order || null,
      },
      organizationId: detailsData?.id,
    });
  };

  const handleFiltersReset = () => {
    setUserSortedFields([]);
    setTableParams({
      pagination: tableParams.pagination,
      filters: {},
      sorter: {
        field: '',
        order: null,
      },
      organizationId: detailsData?.id,
    });
  };

  const getVisibleExportColumns = useCallback(() => {
    const tableSettings = appLocalStorage.getItem(EStorageNames.tableSettings);
    const columnsState = tableSettings?.[
      ETableStoragePaths.ORGANIZATION
    ] as TColumnsState[];
    if (!columnsState) {
      return Object.values(COLUMN_KEY_TO_EXPORT_NAME);
    }
    return columnsState
      .filter(col => col.show)
      .map(col => COLUMN_KEY_TO_EXPORT_NAME[col.key as string])
      .filter(Boolean);
  }, []);

  const buildExportFilters = useCallback(
    (ids?: string[]) => {
      const filters: Record<string, unknown> = {};
      if (!isAllTab) {
        filters.category = activeTab;
      }
      if (ids?.length) {
        filters.ids = ids;
      } else if (tableParams.filters.resourceIds?.length) {
        filters.ids = tableParams.filters.resourceIds;
      }
      if (tableParams.filters.ageGroup?.length) {
        filters.ageGroups = tableParams.filters.ageGroup;
      }
      if (tableParams.filters.language?.length) {
        filters.languages = tableParams.filters.language;
      }
      return filters;
    },
    [tableParams.filters, isAllTab, activeTab],
  );

  const buildExportSort = useCallback(() => {
    if (!tableParams.sorter.field || !tableParams.sorter.order)
      return undefined;
    const field = SORT_FIELD_MAP[tableParams.sorter.field];
    if (!field) return undefined;
    return {
      field,
      direction: tableParams.sorter.order === 'descend' ? 'DESC' : 'ASC',
    };
  }, [tableParams.sorter]);

  const handleExportAll = useCallback(async () => {
    if (!detailsData?.id) return;
    try {
      const sort = buildExportSort();
      await exportKitInventory({
        organizationId: detailsData.id,
        columns: getVisibleExportColumns(),
        filters: buildExportFilters(),
        ...(sort && { sort }),
      }).unwrap();
    } catch {
      showToastNotification({
        message: 'Export failed. Please try again.',
        type: ENotificationType.ERROR,
      });
    }
  }, [
    detailsData?.id,
    exportKitInventory,
    getVisibleExportColumns,
    buildExportFilters,
    buildExportSort,
    showToastNotification,
  ]);

  const handleExportSelected = useCallback(async () => {
    if (!detailsData?.id) return;
    const { selectedRowsKeys } = selectedRows;
    if (!selectedRowsKeys?.length) {
      showToastNotification({
        message: 'Please select at least one row to export',
        type: ENotificationType.ERROR,
      });
      return;
    }
    try {
      const ids = selectedRowsKeys.map(key => String(key));
      const sort = buildExportSort();
      await exportKitInventory({
        organizationId: detailsData.id,
        columns: getVisibleExportColumns(),
        filters: buildExportFilters(ids),
        ...(sort && { sort }),
      }).unwrap();
    } catch {
      showToastNotification({
        message: 'Export failed. Please try again.',
        type: ENotificationType.ERROR,
      });
    }
  }, [
    detailsData?.id,
    selectedRows,
    exportKitInventory,
    getVisibleExportColumns,
    buildExportFilters,
    buildExportSort,
    showToastNotification,
  ]);

  const handleAddNote = () => {
    setIsNotesModalVisible(true);
  };

  const handleCloseNotesModal = () => {
    setIsNotesModalVisible(false);
  };

  const handleShowHistory = () => {
    setIsHistoryModalVisible(true);
  };

  const handleCloseHistoryModal = () => {
    setIsHistoryModalVisible(false);
  };

  const handleTabChange = (key: string) => {
    setActiveTab(key);
    try {
      localStorage.setItem(ACTIVE_TAB_STORAGE_KEY, key);
    } catch {
      // ignore localStorage errors
    }
    // Reset table state on tab switch
    setUserSortedFields([]);
    setTableParams(prev => ({
      ...DEFAULT_TABLE_PARAMS,
      organizationId: prev.organizationId,
    }));
  };

  const handleInventoryChangeAndSave = async (
    record: TKitInventoryItem,
    value: number,
  ) => {
    const previousValue = editedValues[record.key] ?? record.inventory;
    setEditedValues(prev => ({
      ...prev,
      [record.key]: value,
    }));

    try {
      setUpdatingKey(record.key);
      const result = await updateKitInventory({
        inventoryId: record.key,
        inventory: value,
      });

      if ('data' in result || !('error' in result)) {
        showToastNotification({
          message: `Inventory for ${record.name} has been updated successfully`,
          type: ENotificationType.SUCCESS,
        });
      } else {
        // revert on failure
        setEditedValues(prev => ({ ...prev, [record.key]: previousValue }));
        showToastNotification({
          message: 'Failed to update inventory. Please try again.',
          type: ENotificationType.ERROR,
        });
      }
    } catch (error) {
      setEditedValues(prev => ({ ...prev, [record.key]: previousValue }));
      showToastNotification({
        message: 'Failed to update inventory. Please try again.',
        type: ENotificationType.ERROR,
      });
    } finally {
      setUpdatingKey(null);
    }
  };

  const columns = useMemo<TColumnType<TKitInventoryItem>[]>(
    () => [
      {
        title: t('table.KIT_NAME', 'Kit Name and language'),
        dataIndex: 'name',
        key: 'resourceIds',
        filters: allFilters.resourceIds,
        filteredValue: (tableParams.filters.resourceIds as string[]) || null,
      },
      {
        title: t('table.AGE_GROUP', 'Age group'),
        dataIndex: 'ageGroup',
        key: 'ageGroup',
        filters: allFilters.ageGroup,
        filteredValue: (tableParams.filters.ageGroup as string[]) || null,
        onFilter: (value, record) => record.ageGroupEnum === value,
      },
      {
        title: t('table.LANGUAGE', 'Language'),
        dataIndex: 'language',
        key: 'language',
        filters: allFilters.language,
        filteredValue: (tableParams.filters.language as string[]) || null,
      },
      {
        title: t('table.QTY_ON_HAND', 'Qty On Hand'),
        dataIndex: 'inventory',
        key: 'inventory',
        align: 'center',
        sorter: true,
        sortOrder:
          userSortedFields.includes('inventory') &&
          tableParams.sorter.field === 'inventory'
            ? tableParams.sorter.order
            : null,
        render: (_, record) => (
          <Flex justify="center">
            <PlusMinusInput
              value={editedValues[record.key] ?? record.inventory}
              onChange={value => handleInventoryChangeAndSave(record, value)}
              min={0}
              width={100}
              disabled={!canEdit || updatingKey === record.key}
            />
          </Flex>
        ),
      },
      {
        title: t('table.LAST_RECEIVED', 'Last Received'),
        dataIndex: 'lastReceivedDate',
        key: 'lastReceivedDate',
        align: 'center',
        sorter: true,
        sortOrder:
          userSortedFields.includes('lastReceivedDate') &&
          tableParams.sorter.field === 'lastReceivedDate'
            ? tableParams.sorter.order
            : null,
      },
      // Actions column removed as updates now happen inline
    ],
    [
      allFilters,
      tableParams.filters,
      userSortedFields,
      tableParams.sorter.field,
      tableParams.sorter.order,
      tableParams.filters.resourceIds,
      t,
    ],
  );

  const tableHeaderActions = useMemo(
    () => (
      <Space>
        <Button
          icon={<NotebookOutline size={24} />}
          variant="default"
          onClick={handleAddNote}
          data-cy="addNote_btn"
          style={{ fontWeight: 600 }}
          disabled={!canEdit}
        >
          {t('buttons.ADD_NOTE', 'Add Note')}
        </Button>
        <Button
          icon={<RefreshOutline size={24} />}
          variant="default"
          onClick={handleShowHistory}
          data-cy="showHistory_btn"
          style={{ fontWeight: 600 }}
        >
          {t('buttons.SHOW_HISTORY', 'Show history')}
        </Button>
        <ExportDropdown
          onExportAll={handleExportAll}
          onExportSelected={handleExportSelected}
          isExporting={isExporting}
        />
      </Space>
    ),
    [t, canEdit, handleExportAll, handleExportSelected, isExporting],
  );

  return (
    <div data-cy="kit_inventory_content">
      <TabsContainer>
        {KIT_INVENTORY_TABS.map((tab, index) => (
          <TabButton
            key={tab.key}
            type="button"
            isActive={activeTab === tab.key}
            isFirst={index === 0}
            isLast={index === KIT_INVENTORY_TABS.length - 1}
            onClick={() => handleTabChange(tab.key)}
          >
            {tab.label}
          </TabButton>
        ))}
      </TabsContainer>
      <Card key={activeTab} styles={{ body: { padding: 0 } }}>
        <Table
          dataSource={kitInventoryData}
          tableTitle={t('TABLE_TITLE', 'Kit Inventory')}
          columns={columns as TColumnType<unknown>[]}
          pagination={{
            pageSize: tableParams.pagination.pageSize,
            current: tableParams.pagination.current,
            total: totalElements,
            showSizeChanger: true,
            pageSizeOptions: ['5', '10', '20', '50'],
            showTotal: total =>
              `${t('table.TOTAL_ITEMS', `Total ${total} items`)}`,
          }}
          storagePath={ETableStoragePaths.ORGANIZATION}
          headerActions={tableHeaderActions}
          loading={
            isKitInventoryLoading || isKitInventoryFetching || isUpdating
          }
          onChange={handleTableChange}
          hasFilters={hasFilters}
          onFiltersReset={handleFiltersReset}
          tabHeight={110}
          rowKey={record => (record as TKitInventoryItem).key}
          selectedRows={selectedRows}
          setSelectedRows={setSelectedRows}
          key={`kit-inventory-${kitInventoryData.length}`}
        />
      </Card>

      <OrganizationNotesModal
        visible={isNotesModalVisible}
        onClose={handleCloseNotesModal}
        organizationId={detailsData?.id || ''}
      />
      <OrderHistoryModal
        visible={isHistoryModalVisible}
        onClose={handleCloseHistoryModal}
        organizationId={detailsData?.id || ''}
        context="organization"
      />
    </div>
  );
};

export default OrganizationKitsInventoryManagementTab;
